import styled from "styled-components";
import { transparentize } from "polished";
import { Root, Track, Range, Thumb } from "@radix-ui/react-slider";

import { numericFontStyles } from "_/style";

export type SliderSize = "small" | "large";

const thumbSize = (sliderSize: SliderSize) =>
  sliderSize === "small" ? 14 : 16;

const trackThickness = (sliderSize: SliderSize) =>
  sliderSize === "small" ? 4 : 5;

export const SliderRoot = styled(Root)<{ $size: SliderSize }>`
  position: relative;
  display: flex;
  align-items: center;
  user-select: none;
  touch-action: none;
  width: 100%;
  height: ${(p) => thumbSize(p.$size)}px;
  cursor: pointer;

  &[data-orientation="vertical"] {
    height: 100%;
    width: ${(p) => thumbSize(p.$size)}px;
    flex-direction: column;
  }
`;

export const SliderTrack = styled(Track)<{
  $size: SliderSize;
  $background?: string;
}>`
  background: ${(p) =>
    p.$background ?? transparentize(0.5, p.theme.color.brand.blue)};
  position: relative;
  flex-grow: 1;
  height: ${(p) => trackThickness(p.$size)}px;
  margin: 0 ${(p) => thumbSize(p.$size) / 2}px;

  &[data-orientation="vertical"] {
    margin: ${(p) => thumbSize(p.$size) / 2}px 0;
    height: 100%;
    width: ${(p) => trackThickness(p.$size)}px;
  }
`;

export const SliderRange = styled(Range)<{ $background?: string }>`
  position: absolute;
  background: ${(p) => p.$background ?? p.theme.color.brand.blue};
  height: 100%;

  &[data-orientation="vertical"] {
    height: unset;
    width: 100%;
    flex-direction: column;
  }
`;

const TRANSITION_DURATION = "0.07s";

export const SliderThumb = styled(Thumb)<{
  $size: SliderSize;
  $color?: string;
  $visualWidth?: number;
}>`
  display: block;
  position: relative;
  width: ${(p) => thumbSize(p.$size)}px;
  height: ${(p) => thumbSize(p.$size)}px;
  background-color: ${(p) =>
    p.$visualWidth ? "transparent" : (p.$color ?? p.theme.color.brand.blue)};
  border-radius: 3px;
  z-index: 1;
  transition:
    box-shadow ${TRANSITION_DURATION} ease,
    border-width ${TRANSITION_DURATION} ease,
    background-color ${TRANSITION_DURATION} ease,
    border-color ${TRANSITION_DURATION} ease,
    outline-width 0s;

  &:hover {
    background-color: ${(p) =>
      p.$visualWidth ? "transparent" : (p.$color ?? p.theme.color.brand.blue)};
  }

  &::after {
    content: ${(p) => (p.$visualWidth ? '""' : "none")};
    position: absolute;
    inset-block: 0;
    left: 50%;
    width: ${(p) => p.$visualWidth ?? 0}px;
    transform: translateX(-50%);
    border-radius: 2px;
    background-color: ${(p) => p.$color ?? p.theme.color.brand.blue};
  }

  &:focus {
    /*
      Use box-shadow to emulate outline to avoid a Firefox bug where built-in
      focus animations of outline override ours. This is the same trick that our
      slider control already uses. Safari sadly uses box-shadow (and is even
      less overridable than Firefox's use of outline for the focus animation), but
      our customer base is 95%+ Windows users so we'll let Safari have bouncy
      checkboxes.
    */
    outline: none;
    box-shadow: ${(p) =>
      p.$visualWidth ? "none" : `0 0 0 2px ${p.theme.text.default}`};
  }

  &:focus::after {
    box-shadow: ${(p) =>
      p.$visualWidth ? `0 0 0 2px ${p.theme.text.default}` : "none"};
  }
`;

export const Tooltip = styled.div`
  font-size: 0.875rem;
  ${numericFontStyles}
  padding: 0.5rem;
  z-index: 2;
  border-radius: 0.25rem;
  background-color: ${(p) =>
    transparentize(0.2, p.theme.background.quaternary)};

  /* Interaction/animation */
  pointer-events: none; /* never steal hover/drag */
  transition: opacity 0.2s ease-in-out;
`;
